"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _react = _interopRequireDefault(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _componentsBase = require("@telus-uds/components-base");
var _styledComponents = _interopRequireDefault(require("styled-components"));
var _videoText = _interopRequireDefault(require("../../../videoText"));
var _utils = require("../../../../utils");
var _jsxRuntime = require("react/jsx-runtime");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
const [selectProps, selectedSystemPropTypes] = (0, _componentsBase.selectSystemProps)([_utils.htmlAttrs]);
const ProgressBarContainer = /*#__PURE__*/_styledComponents.default.div.withConfig({
  displayName: "VideoProgressBar__ProgressBarContainer",
  componentId: "components-web__sc-d9tm07-0"
})({
  display: 'flex',
  width: '100%',
  alignItems: 'center'
});
const sharedStyles = _ref => {
  let {
    thumbHeight,
    thumbWidth,
    thumbBackground,
    trackGradientStart,
    trackGradientEnd
  } = _ref;
  return {
    thumb: {
      appearance: 'none',
      height: thumbHeight,
      width: thumbWidth,
      borderRadius: '50%',
      background: thumbBackground,
      cursor: 'pointer',
      marginTop: -3,
      '&:hover': {
        transform: 'scale(1.5)'
      }
    },
    track: videoBufferDisplay => ({
      width: '100%',
      height: 2,
      cursor: 'pointer',
      borderRadius: 1.3,
      background: `linear-gradient(to right, ${trackGradientStart} 0%,${trackGradientEnd} ${videoBufferDisplay - 0.01}% ,rgba(255,255,255,0.5) ${videoBufferDisplay}%)` // TODO: replace with opaque white from palette
    })
  };
};
const StyledProgressBar = /*#__PURE__*/_styledComponents.default.input.attrs(_ref2 => {
  let {
    videoCurrentTime
  } = _ref2;
  return {
    value: videoCurrentTime
  };
}).withConfig({
  displayName: "VideoProgressBar__StyledProgressBar",
  componentId: "components-web__sc-d9tm07-1"
})(_ref3 => {
  let {
    videoBufferDisplay,
    rangeBackground,
    ...sharedProps
  } = _ref3;
  return {
    width: '100%',
    cursor: 'pointer',
    padding: '5px 0',
    'input[type=range]&': {
      appearance: 'none',
      width: '100%',
      background: rangeBackground
    },
    'input[type=range]&:focus': {
      outline: 'none'
    },
    'input[type=range]&::-webkit-slider-thumb': sharedStyles(sharedProps).thumb,
    'input[type=range]&::-moz-range-thumb': {
      ...sharedStyles(sharedProps).thumb,
      border: 'none'
    },
    'input[type=range]&::-ms-thumb': {
      ...sharedStyles(sharedProps).thumb,
      margin: 0,
      border: 'none'
    },
    'input[type=range]&::-webkit-slider-runnable-track': sharedStyles(sharedProps).track(videoBufferDisplay),
    'input[type=range]&::-moz-range-track': sharedStyles(sharedProps).track(videoBufferDisplay),
    'input[type=range]&::-ms-track': {
      ...sharedStyles(sharedProps).track(videoBufferDisplay),
      margin: '6px 0',
      border: 'none'
    },
    'input[type=range]&::-ms-fill-lower': {
      background: rangeBackground
    },
    'input[type=range]&::-ms-tooltip': {
      display: 'none'
    }
  };
});
const StyledTimestamp = /*#__PURE__*/_styledComponents.default.span.withConfig({
  displayName: "VideoProgressBar__StyledTimestamp",
  componentId: "components-web__sc-d9tm07-2"
})(_ref4 => {
  let {
    margin
  } = _ref4;
  return {
    margin
  };
});
// TODO: unify with the helper from `VideoSplash`
function getTimestamp(duration) {
  const minutes = Math.floor(duration / 60);
  const seconds = Math.floor(duration - 60 * minutes);
  return `${minutes}:${seconds < 10 ? 0 : ''}${seconds}`;
}
const VideoProgressBar = _ref5 => {
  let {
    copy = 'en',
    videoLength,
    videoCurrentTime,
    videoBufferEnd,
    playing,
    setSeek,
    resetInactivityTimer,
    tokens,
    variant,
    ...rest
  } = _ref5;
  const {
    thumbHeight,
    thumbWidth,
    thumbBackground,
    timestampMarginLeft,
    timestampMarginRight,
    remainingTimestampMarginLeft,
    remainingTimestampMarginRight,
    trackGradientStart,
    trackGradientEnd,
    rangeBackground
  } = (0, _componentsBase.useThemeTokens)('VideoProgressBar', tokens, variant, {
    playing
  });
  const videoProgressBar = /*#__PURE__*/_react.default.createRef();
  const handleVideoSkip = () => {
    setSeek(videoProgressBar.current.value);
  };
  const videoBufferDisplay = videoBufferEnd / videoLength * 100;
  const isVideoUnplayed = videoCurrentTime === -1;
  const currentTime = isVideoUnplayed ? 0 : videoCurrentTime;
  const remainingTime = isVideoUnplayed ? videoLength : videoLength - videoCurrentTime;
  const currentTimestamp = getTimestamp(currentTime);
  const remainingTimestamp = getTimestamp(remainingTime);
  const sharedProps = {
    thumbWidth,
    thumbHeight,
    thumbBackground,
    trackGradientStart,
    trackGradientEnd,
    rangeBackground
  };
  return /*#__PURE__*/(0, _jsxRuntime.jsxs)(ProgressBarContainer, {
    ...selectProps(rest),
    children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(StyledTimestamp, {
      margin: `0 ${timestampMarginRight}px 0 ${timestampMarginLeft}px`,
      children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_componentsBase.Typography, {
        variant: {
          inverse: true
        },
        children: currentTimestamp
      })
    }), /*#__PURE__*/(0, _jsxRuntime.jsx)(StyledProgressBar, {
      "aria-label": _videoText.default[copy].videoProgressBarLabel,
      type: "range",
      step: "any",
      max: videoLength,
      videoCurrentTime: videoCurrentTime,
      onChange: handleVideoSkip,
      onFocus: resetInactivityTimer,
      videoBufferDisplay: videoBufferDisplay,
      ref: videoProgressBar,
      tabIndex: "-1",
      ...sharedProps
    }), /*#__PURE__*/(0, _jsxRuntime.jsx)(StyledTimestamp, {
      margin: `0 ${remainingTimestampMarginRight}px 0 ${remainingTimestampMarginLeft}px`,
      children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_componentsBase.Typography, {
        variant: {
          inverse: true
        },
        children: remainingTimestamp
      })
    })]
  });
};
VideoProgressBar.propTypes = {
  ...selectedSystemPropTypes,
  copy: _propTypes.default.oneOf(['en', 'fr']),
  videoLength: _propTypes.default.number.isRequired,
  videoCurrentTime: _propTypes.default.number.isRequired,
  videoBufferEnd: _propTypes.default.number.isRequired,
  setSeek: _propTypes.default.func.isRequired,
  resetInactivityTimer: _propTypes.default.func.isRequired
};
var _default = exports.default = VideoProgressBar;